import startCase from "lodash/startCase";

import { getSellTreezConfigBaseUrlFromEndpoint } from "../util/sellTreezStoreConfig";

const openSearchApiKey = process.env.REACT_APP_OPEN_SEARCH_API_KEY ?? "";

type SellTreezErrorCause<TReq = RequestInit> = {
    data: unknown;
    status: number;
    req: TReq;
    endpoint: string;
};

class SellTreezError extends Error {
    override cause?: SellTreezErrorCause;

    constructor(message: string, { cause }: { cause: SellTreezErrorCause }) {
        super(message);
        this.name = "SellTreezError";
        this.cause = cause;
    }
}

function getSellTreezApiUrl(): string {
    const endpointUrl = process.env.REACT_APP_OPEN_SEARCH_ENDPOINT;
    if (!endpointUrl) {
        throw new Error(
            "getSellTreezApiUrl: REACT_APP_OPEN_SEARCH_ENDPOINT is not set",
        );
    }
    const base = getSellTreezConfigBaseUrlFromEndpoint(endpointUrl);
    if (!base) {
        throw new Error(
            "getSellTreezApiUrl: could not derive base URL from endpoint",
        );
    }
    return base;
}

export class SellTreezClient {
    private readonly _apiUrl: string;

    private readonly _entityId: string;

    constructor(entityId: string) {
        this._apiUrl = getSellTreezApiUrl();
        this._entityId = entityId;
    }

    private buildHeaders = (
        body: RequestInit["body"],
        customHeaders: RequestInit["headers"],
    ): HeadersInit => {
        return {
            ...(body ? { "Content-Type": "application/json" } : {}),
            "Entity-Id": this._entityId,
            "X-Api-Key": openSearchApiKey,
            ...customHeaders,
        };
    };

    private request = async (
        endpoint: string,
        { method, body, headers: customHeaders, ...customConfig }: RequestInit,
    ): Promise<unknown> => {
        const config: RequestInit = {
            method,
            body,
            headers: this.buildHeaders(body, customHeaders),
            ...customConfig,
        };
        const response = await fetch(`${this._apiUrl}/${endpoint}`, config);
        const data = await response.json().catch(() => ({}));

        if (response.ok) {
            return data;
        }

        const detail =
            (data as { detail?: unknown; description?: unknown })?.detail ??
            (data as { description?: unknown })?.description ??
            "request_failed";

        throw new SellTreezError(
            startCase(String(detail).replace(/_/g, " ").toLowerCase()),
            {
                cause: {
                    data,
                    status: response.status,
                    req: config,
                    endpoint,
                },
            },
        );
    };

    get = async <T extends object>(
        endpoint: string,
        signal?: AbortSignal,
    ): Promise<T> => {
        return this.request(endpoint, { method: "GET", signal }) as Promise<T>;
    };
}

export { SellTreezError };
export type { SellTreezErrorCause };
